import sendGTMEvent from '../../helpers/sendGTMEvent';
import getPriceSummaryValues from './getPriceSummaryValues';
import getActivePackage from '../../helpers/getActivePackage';
import getActivePackageObject from '../../helpers/getActivePackageObject';

// push Universal Analytics style event
const sendUaEvent = (data) => {
  if (window.dataLayer) {
    window.dataLayer.push(data);
  }
};

// push GA4 style event
const sendGa4Event = (products, totalPriceInDollars, eventType) => {
  const eventObject = {
    value: totalPriceInDollars,
    products,
  };
  sendGTMEvent(eventObject, eventType);
};

class EventsHelper {
  constructor(pressRelease, pressReleaseMetadata, sitesList, manualPrPrice = null) {
    this.pressRelease = pressRelease;
    this.pressReleaseMetadata = pressReleaseMetadata;
    this.sitesList = sitesList;
    this.addonsList = sitesList
      .map((site) => site.topic_newsletter_add_on_options)
      .flat();

    this.currentSite = sitesList
      .find((site) => site.site_id === pressReleaseMetadata.current_site_pk);
    this.selectedSites = sitesList.filter((site) => site.selected);
    this.selectedAddons = this.selectedSites
      .map((site) => (
        site.topic_newsletter_add_on_options
          .filter((newsletter) => newsletter.selected)
      )).flat();
    // Creating this property instead of relying on pressRelease.has_multimedia_add_on
    // since that is not set to true until after payment succeess.
    this.hasMultimediaAddOn = pressRelease.multimedia_add_ons.length > 0;
    this.manualPrPrice = manualPrPrice;
  }

  getTotalPriceInDollars({ numIncomingSites = 0, numIncomingNlAddOns = 0 } = {}) {
    const { totalPrice } = getPriceSummaryValues(
      this.pressRelease,
      this.pressReleaseMetadata,
      this.sitesList,
      numIncomingSites,
      numIncomingNlAddOns,
      this.manualPrPrice,
    );
    return totalPrice / 100;
  }

  static sendCheckoutStep(screen, url, totalPriceInDollarsLatest) {
    sendUaEvent({
      event: 'pressrelease_checkout_step',
      pressrelease_checkout_screen: screen,
      pressrelease_url: url,
      pressrelease_cost: totalPriceInDollarsLatest,
    });
  }

  getPublicationProduct(sitePk) {
    const selectedSite = this.sitesList
      .find((s) => s.site_id === sitePk);
    // If we have a manualPrPrice, use it as default over pressrelease.price_per_press_release
    const price = this.manualPrPrice !== null
      ? this.manualPrPrice / 100
      : this.pressRelease.price_per_press_release / 100;
    return {
      id: sitePk,
      v: 11,
      name: selectedSite.site_name,
      price,
      quantity: 1,
      site: this.currentSite.site_name,
      type: 'Publication',
    };
  }

  getNewsletterProduct(addonId) {
    const newsletter = this.addonsList
      .find((a) => a.newsletter_schedule_id === addonId);
    if (!newsletter) {
      return null;
    }
    return {
      id: newsletter.newsletter_schedule_id,
      v: 11,
      name: `${newsletter.site_name}: ${newsletter.top_header}`,
      price: this.pressReleaseMetadata.topic_newsletter_add_on_price / 100,
      quantity: 1,
      site: this.currentSite.site_name,
      type: 'Newsletter',
    };
  }

  getMultimediaAddOnProduct() {
    return {
      v: 11,
      name: 'Multimedia Add On',
      price: this.pressReleaseMetadata.multimedia_add_on_price / 100,
      quantity: 1,
      site: this.currentSite.site_name,
      type: 'Multimedia',
    };
  }

  getProduct(objectId, productType) {
    switch (productType) {
      case 'publication':
        return this.getPublicationProduct(objectId);
      case 'newsletter':
        return this.getNewsletterProduct(objectId);
      default:
        return null;
    }
  }

  getProducts() {
    const publicationProducts = this.selectedSites
      .map((site) => this.getPublicationProduct(site.site_id));
    const newsletterProducts = this.selectedAddons
      .map((addon) => this.getNewsletterProduct(addon.newsletter_schedule_id));
    const multiMediaProduct = this.hasMultimediaAddOn ? [this.getMultimediaAddOnProduct()] : [];

    // concat product arrays
    return [...publicationProducts, ...newsletterProducts, ...multiMediaProduct];
  }

  triggerAddToCartEvent(objectId, productType, synchronouslyAddedItemCounts) {
    this.setManualPrPrice(synchronouslyAddedItemCounts.numIncomingSites);
    const product = this.getProduct(objectId, productType);
    if (product) {
      sendGa4Event(
        [product],
        this.getTotalPriceInDollars(synchronouslyAddedItemCounts),
        'add',
      );
    }
  }

  triggerAddMultipleToCartEvent(objectIds, productType, synchronouslyAddedItemCounts) {
    this.setManualPrPrice(synchronouslyAddedItemCounts.numIncomingSites);
    const products = objectIds.map((objId) => this.getProduct(objId, productType));
    if (products.filter((x) => !!x).length) {
      sendGa4Event(
        products,
        this.getTotalPriceInDollars(synchronouslyAddedItemCounts),
        'add',
      );
    }
  }

  triggerRemoveFromCartEvent(objectId, productType, synchronouslyAddedItemCounts) {
    const product = this.getProduct(objectId, productType);
    // Change the price per press release after getProduct() so that the price of the
    // removed item listed in the event is accurate, and then the updated total price
    // reflects a change in package pricing if necessary
    this.setManualPrPrice(synchronouslyAddedItemCounts.numIncomingSites);

    if (product) {
      sendGa4Event(
        [product],
        this.getTotalPriceInDollars(synchronouslyAddedItemCounts),
        'remove',
      );
    }
  }

  handleBeginCheckout() {
    sendGa4Event(
      this.getProducts(),
      this.getTotalPriceInDollars(),
      'begin_checkout',
    );
  }

  handleAddPaymentInfo() {
    sendGa4Event(
      this.getProducts(),
      this.getTotalPriceInDollars(),
      'add_payment_info',
    );
  }

  handlePurchase() {
    sendGa4Event(
      this.getProducts(),
      this.getTotalPriceInDollars(),
      'purchase',
    );
  }

  setManualPrPrice(numSites) {
    const numPressReleasesSelected = this.pressRelease.crosspost_sites.length + numSites;
    const activePkg = getActivePackage(
      numPressReleasesSelected,
      this.pressReleaseMetadata.crosspost_packages,
    );
    const activePkgObject = getActivePackageObject(
      activePkg,
      this.pressReleaseMetadata.crosspost_packages,
    );
    this.manualPrPrice = activePkgObject.price_per_press_release;
  }
}

export default EventsHelper;
